import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row, Table, Tag } from 'antd';
import SuperAdminAPI from '../../../services/SuperAdminAPI';

const QueueHealthPanel = () => {
  const [loading, setLoading] = useState(false);
  const [queues, setQueues] = useState([]);
  const [jobMetrics, setJobMetrics] = useState({});
  const [circuitBreakers, setCircuitBreakers] = useState({ feature: {}, provider: {} });
  const [workerStatus, setWorkerStatus] = useState({});

  const loadData = async () => {
    setLoading(true);
    try {
      const [queueStatus, jobMetricsData, circuitData, workerData] = await Promise.all([
        SuperAdminAPI.getQueueStatus(),
        SuperAdminAPI.getJobMetrics(),
        SuperAdminAPI.getCircuitBreakers(),
        SuperAdminAPI.getWorkerHealthSnapshot(false),
      ]);

      const queueItems = Object.values(queueStatus || {}).filter(Boolean);
      setQueues(queueItems);
      setJobMetrics(jobMetricsData?.metrics || {});
      setCircuitBreakers(circuitData || { feature: {}, provider: {} });
      setWorkerStatus(workerData?.workerStatus || workerData?.workers || {});
    } catch (error) {
      console.error('QueueHealthPanel: failed to load data', error);
      setQueues([]);
      setJobMetrics({});
      setCircuitBreakers({ feature: {}, provider: {} });
      setWorkerStatus({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const circuitRows = useMemo(() => {
    const rows = [];
    Object.entries(circuitBreakers?.feature || {}).forEach(([name, info]) => {
      rows.push({ scope: 'Feature', name, open: info?.open, reason: info?.reason });
    });
    Object.entries(circuitBreakers?.provider || {}).forEach(([name, info]) => {
      rows.push({ scope: 'Provider', name, open: info?.open, reason: info?.reason });
    });
    return rows;
  }, [circuitBreakers]);

  const workerRows = useMemo(() => {
    return Object.entries(workerStatus || {}).map(([name, info]) => ({
      name,
      healthy: info?.healthy ?? info?.running ?? false,
      reason: info?.reason || info?.details?.status || 'unknown',
    }));
  }, [workerStatus]);

  const jobMetricRows = useMemo(() => {
    return Object.entries(jobMetrics || {}).map(([feature, values]) => ({
      feature,
      ...values,
    }));
  }, [jobMetrics]);

  const circuitColumns = [
    { title: 'Scope', dataIndex: 'scope', key: 'scope', width: 120 },
    { title: 'Name', dataIndex: 'name', key: 'name', width: 200 },
    {
      title: 'Status',
      dataIndex: 'open',
      key: 'open',
      width: 120,
      render: value => (
        <Tag color={value ? 'red' : 'green'}>{value ? 'OPEN' : 'CLOSED'}</Tag>
      ),
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason' },
  ];

  const workerColumns = [
    { title: 'Worker', dataIndex: 'name', key: 'name' },
    {
      title: 'Healthy',
      dataIndex: 'healthy',
      key: 'healthy',
      render: value => <Tag color={value ? 'green' : 'volcano'}>{value ? 'Yes' : 'No'}</Tag>,
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason' },
  ];

  return (
    <Card
      title="Queue Health"
      extra={
        <Button onClick={loadData} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Row gutter={[16, 16]}>
        {queues.map(queue => (
          <Col key={queue.name} xs={24} sm={12} md={8} lg={6}>
            <Card size="small" title={queue.name}>
              <div>Waiting: {queue.waiting}</div>
              <div>Active: {queue.active}</div>
              <div>Delayed: {queue.delayed}</div>
              <div>Failed: {queue.failed}</div>
              <div>Completed: {queue.completed}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card title="Circuit Breakers" style={{ marginTop: 16 }}>
        <Table
          rowKey={record => `${record.scope}-${record.name}`}
          columns={circuitColumns}
          dataSource={circuitRows}
          pagination={{ pageSize: 8 }}
          loading={loading}
        />
      </Card>

      <Card title="Workers" style={{ marginTop: 16 }}>
        <Table
          rowKey="name"
          columns={workerColumns}
          dataSource={workerRows}
          pagination={{ pageSize: 8 }}
          loading={loading}
        />
      </Card>

      <Card title="Job Metrics" style={{ marginTop: 16 }}>
        <Table
          rowKey="feature"
          columns={[
            { title: 'Feature', dataIndex: 'feature', key: 'feature' },
            { title: 'Queued', dataIndex: 'queued', key: 'queued' },
            { title: 'Existing', dataIndex: 'existing', key: 'existing' },
            { title: 'Throttled', dataIndex: 'throttled', key: 'throttled' },
            { title: 'Rejected', dataIndex: 'rejected', key: 'rejected' },
            { title: 'Unavailable', dataIndex: 'queued_unavailable', key: 'queued_unavailable' },
            { title: 'Disabled', dataIndex: 'disabled', key: 'disabled' },
          ]}
          dataSource={jobMetricRows}
          pagination={{ pageSize: 6 }}
          loading={loading}
        />
      </Card>
    </Card>
  );
};

export default QueueHealthPanel;
